// How the Lines tab arranges a quote: bundles with their options under them,
// volume tiers in words, and the revenue schedule as a month-by-line table.
// Pure, so it can be tested without the page.
import type { BuilderLine, PriceAdjustment, RevenueScheduleRow } from './quotesApi';

/** Lines in display order: each commercial line, then its options. Orphans (a missing parent) count as commercial. */
export function orderLines(lines: BuilderLine[]): BuilderLine[] {
  const ids = new Set(lines.map((l) => l.id));
  const isTop = (l: BuilderLine) => !l.parent_line_id || !ids.has(l.parent_line_id);
  return lines.filter(isTop).flatMap((top) => [top, ...lines.filter((l) => l.parent_line_id === top.id)]);
}

/** A bundle: a line other lines name as their parent. */
export const bundleIds = (lines: BuilderLine[]): Set<string> =>
  new Set(lines.map((l) => l.parent_line_id).filter((p): p is string => typeof p === 'string'));

/** "0–199: list · 200+: 25% off" for a tooltip. */
export function describeTiers(adjustment: PriceAdjustment | null, money: (n: number) => string): string {
  if (!adjustment?.tiers.length) return '';
  const range = (t: PriceAdjustment['tiers'][number]) => (t.upper === null ? `${t.lower}+` : `${t.lower}–${t.upper - 1}`);
  const effect = (t: PriceAdjustment['tiers'][number]) =>
    t.type === 'percent' ? (t.value === 0 ? 'list' : `${t.value}% off`) : t.type === 'amount' ? `${money(t.value)} off each` : `${money(t.value)} each`;
  const tiers = adjustment.tiers.map((t) => `${range(t)}: ${effect(t)}`).join(' · ');
  return `${adjustment.method === 'slab' ? 'Slab' : 'Range'} tiers: ${tiers}`;
}

export interface ScheduleMonth {
  key: string;
  label: string;
  /** Amount by commercial line id. */
  byLine: Record<string, number>;
  revenue: number;
  hours: number | null;
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const round1 = (n: number) => Math.round(n * 10) / 10;

/** "Jan 2027" for a YYYY-MM-DD period start. */
export const monthLabel = (date: string): string =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });

/** Schedule rows pivoted to one row per month (by period start), a column per line, totals of revenue and hours. */
export function scheduleByMonth(rows: RevenueScheduleRow[]): ScheduleMonth[] {
  const months = new Map<string, ScheduleMonth>();
  for (const row of rows) {
    const key = row.period_start.slice(0, 7);
    const month = months.get(key) ?? { key, label: monthLabel(row.period_start), byLine: {}, revenue: 0, hours: null };
    month.byLine[row.quote_line_id] = round2((month.byLine[row.quote_line_id] ?? 0) + row.amount);
    month.revenue = round2(month.revenue + row.amount);
    if (row.hours !== null) month.hours = round1((month.hours ?? 0) + row.hours);
    months.set(key, month);
  }
  return [...months.values()].sort((a, b) => a.key.localeCompare(b.key));
}

/**
 * Whether the schedule no longer matches the lines: a commercial line without
 * rows, rows for a line that's gone, or a one-time line whose rows don't add
 * up to its net (or a monthly one whose months, but a prorated last, aren't its net).
 */
export function scheduleIsStale(lines: BuilderLine[], rows: RevenueScheduleRow[]): boolean {
  if (!rows.length) return false;
  const commercial = orderLines(lines).filter((l) => !l.parent_line_id);
  const ids = new Set(commercial.map((l) => l.id));
  if (rows.some((r) => !ids.has(r.quote_line_id))) return true;
  // A line edited by hand is meant to differ from the quote.
  return commercial.filter((line) => !line.schedule_edited_at).some((line) => {
    const own = rows.filter((r) => r.quote_line_id === line.id);
    if (!own.length) return true;
    if (line.billing === 'one_time') return Math.abs(own.reduce((t, r) => t + r.amount, 0) - line.net) > 0.01;
    // A term's last month may be prorated, so it isn't compared.
    return own.slice(0, -1).some((r) => Math.abs(r.amount - line.net) > 0.01) || (own.length === 1 && own[0]!.amount - line.net > 0.01);
  });
}
